/**
 * Hide the engine root in the Inspector's scene tree (SCENE_ROOT_SPEC D8, sb#334).
 *
 * The Inspector's built-in "Nodes" section builds its roots from
 * `scene.rootNodes` — and then, for every node in the scene, walks up to its
 * topmost ancestor and adds that too. Both roads lead to the engine MOUNT
 * POINT, so the tree reads `Scene → <document root> → [content]` and the first
 * entry is a runtime node that is not part of the file at all.
 *
 * **There is no supported hide hook.** Inspector 9.11 exposes
 * `ISceneExplorerService.addSection` (add), but nothing to filter, replace or
 * remove a section that another service already registered — and the built-in
 * `NodeExplorerServiceDefinition` is registered ahead of every service an app
 * passes to `ShowInspector` (they are appended after the defaults, and the
 * container instantiates in dependency-then-registration order). So a section
 * of our own could only ever be a SECOND "Nodes" section next to the built-in.
 *
 * What this module does instead is intercept the registration itself: the
 * sections collection is an `ObservableCollection` — the very class the
 * Inspector re-exports — so patching its `add` lets us hand the explorer a
 * WRAPPED Nodes section, keeping every built-in behaviour (icons, per-entity
 * commands, the live add/remove/move observables, drag-and-drop) and changing
 * only the two answers D8 makes wrong:
 *
 * 1. `getRootEntities` — the engine root is replaced by its children, so the
 *    tree starts at the document root ({@link visibleTreeRoots}).
 * 2. `dragDropConfig` — a drop on the section root means "at the top of the
 *    document" and lands under the document root, instead of `parent = null`,
 *    which would take the object out of the document
 *    ({@link resolveTreeDropTarget}); neither structural root can be dragged.
 *
 * The patch is guarded on the section's own shape, installed once, and reports
 * if it never fires — a Babylon upgrade that renames the section or stops
 * routing sections through `ObservableCollection` shows up in the console
 * rather than silently bringing the mount point back.
 */

import type { IDisposable } from '@babylonjs/core/scene';
import type { Node } from '@babylonjs/core/node';
import { ObservableCollection } from '@babylonjs/inspector';
import type { SceneExplorerSection } from '@babylonjs/inspector';
import type { TraceScene } from 'trace-viewer/scene';
import { resolveTreeDropTarget, isStructuralTreeNode, visibleTreeRoots } from './scene-tree-root';

/** The built-in section this module reroots. */
const NODES_SECTION_NAME = 'Nodes';

type SceneProvider = () => TraceScene | null | undefined;

type NodesSection = SceneExplorerSection<Node>;

let installed = false;
let rerooted = false;

/** Whether the Nodes section was actually intercepted (diagnostics, tests). */
export function isSceneTreeRerooted(): boolean {
    return rerooted;
}

/** Does this look like the Inspector's Nodes section? */
function isNodesSection(item: unknown): item is NodesSection {
    if (typeof item !== 'object' || item === null) return false;
    const section = item as Partial<NodesSection>;
    return section.displayName === NODES_SECTION_NAME
        && typeof section.getRootEntities === 'function'
        && typeof section.getEntityChildren === 'function';
}

/**
 * The Nodes section with the engine root taken out of it — same section,
 * two answers replaced.
 */
export function rerootNodesSection(section: NodesSection, getScene: SceneProvider): NodesSection {
    const dragDrop = section.dragDropConfig;
    return {
        ...section,
        getRootEntities: () => visibleTreeRoots(section.getRootEntities(), getScene()),
        ...(dragDrop
            ? {
                dragDropConfig: {
                    canDrag: (entity: Node) =>
                        !isStructuralTreeNode(entity, getScene()) && dragDrop.canDrag(entity),
                    canDrop: (dragged: Node, target: Node | null) => {
                        const resolved = resolveTreeDropTarget(dragged, target, getScene());
                        return resolved !== null && dragDrop.canDrop(dragged, resolved);
                    },
                    onDrop: (dragged: Node, target: Node | null) => {
                        const resolved = resolveTreeDropTarget(dragged, target, getScene());
                        if (resolved) dragDrop.onDrop(dragged, resolved);
                    },
                },
            }
            : {}),
    };
}

/**
 * Start rerooting the Inspector's Nodes section at the document root.
 *
 * Call BEFORE `ShowInspector`: the Inspector's services — including the one
 * that registers the Nodes section — are built when the tool's React root
 * mounts, which is after this call returns. Idempotent.
 */
export function installDocumentRootSceneTree(getScene: SceneProvider): void {
    if (installed) return;
    installed = true;

    const collection = ObservableCollection.prototype as unknown as {
        add: (item: unknown) => IDisposable;
    };
    const originalAdd = collection.add;

    collection.add = function add(this: unknown, item: unknown): IDisposable {
        if (isNodesSection(item)) {
            rerooted = true;
            console.log(
                '[SceneTree] Nodes section rerooted at the document root — the engine mount '
                + 'point is not shown (SCENE_ROOT_SPEC D8)',
            );
            return originalAdd.call(this, rerootNodesSection(item, getScene));
        }
        return originalAdd.call(this, item);
    };
}

/**
 * Warn if the Inspector has mounted and the Nodes section never came through —
 * the one way this can rot silently on a Babylon upgrade.
 */
export function reportSceneTreeReroot(): void {
    if (rerooted) return;
    console.warn(
        '[SceneTree] The Inspector\'s "Nodes" section was not intercepted: the engine scene '
        + 'root may be visible in the tree. Check @babylonjs/inspector for a renamed section '
        + 'or a new scene-explorer extension point (sb#334).',
    );
}
